/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { waitFor } from '@ember/test-waiters';
import { unloadRecord } from 'uplisting-frontend/utils/changeset';
import { SecurityDepositActionKind } from 'uplisting-frontend/models/security-deposit-action';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';

interface IArgs {
  booking: ModelRegistry['booking'];
}

export interface BookingSettleSecurityDepositSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiBookingSettleSecurityDepositComponent extends Component<BookingSettleSecurityDepositSignature> {
  @service intl!: Services['intl'];
  @service notifications!: Services['notifications'];

  @service('repositories/security-deposit-action')
  securityDepositActionRepository!: Services['repositories/security-deposit-action'];

  @tracked showModal = false;
  @tracked isSaving = false;
  @tracked changeset?: GenericChangeset<
    ModelRegistry['security-deposit-action']
  >;

  @cached
  get securityDeposit(): ModelRegistry['security-deposit'] {
    return this.args.booking
      .securityDeposit as ModelRegistry['security-deposit'];
  }

  get amountToKeep(): number {
    return Number(this.changeset?.amount ?? 0);
  }

  get amountToRelease(): number {
    return Math.max(this.depositAmount - this.amountToKeep, 0);
  }

  get formattedAmountToKeep(): string {
    return this.formatAmount(this.amountToKeep);
  }

  get formattedAmountToRelease(): string {
    return this.formatAmount(this.amountToRelease);
  }

  get formattedDepositAmount(): string {
    return this.formatAmount(this.depositAmount);
  }

  get isSubmitDisabled(): boolean {
    const { changeset } = this;

    if (this.isSaving || !changeset) {
      return true;
    }

    return isEmpty(changeset.amount) || changeset.isInvalid;
  }

  @action
  handleShowModal(): void {
    this.changeset = this.buildChangeset();
    this.showModal = true;
  }

  @action
  handleCloseModal(): void {
    const { changeset } = this;

    if (changeset && !this.isSaving) {
      unloadRecord<ModelRegistry['security-deposit-action']>(changeset);

      this.changeset = undefined;
    }

    this.showModal = false;
  }

  @action
  @waitFor
  async handleSettle(): Promise<void> {
    const { changeset } = this;

    if (!changeset) {
      return;
    }

    this.isSaving = true;

    try {
      await changeset.save();

      this.isSaving = false;
      this.showModal = false;

      this.notifications.info(
        'action_bookings_summary.other_actions.security_deposit_settle.notifications.applied',
      );

      await this.args.booking.reload();
    } catch {
      this.isSaving = false;

      this.notifications.error();
    }
  }

  private get depositAmount(): number {
    return this.securityDeposit?.amount ?? 0;
  }

  private buildChangeset(): GenericChangeset<
    ModelRegistry['security-deposit-action']
  > {
    const record = this.securityDepositActionRepository.createRecord({
      booking: this.args.booking,
      kind: SecurityDepositActionKind.settle,
      amount: 0,
    });

    return this.securityDepositActionRepository.buildChangeset(record);
  }

  private formatAmount(amount: number): string {
    return this.intl.formatNumber(amount, {
      style: 'currency',
      currency: this.args.booking.currency,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::SettleSecurityDeposit': typeof UiBookingSettleSecurityDepositComponent;
  }
}
